import type { TimelineItemType } from '../useTimelineTableColumns';
import { getNormalizePlannedDates } from './timelineDateUtils';

export type ItemWithStackingOrder = TimelineItemType & { order?: number; progress?: number };

export const calculateChildLanes = (
  item: TimelineItemType,
  cache?: Map<number, number>,
): number => {
  if (cache) {
    const cached = cache.get(item.id);
    if (cached !== undefined) return cached;
  }
  if (!item.children || item.children.length === 0) {
    cache?.set(item.id, 1);
    return 1;
  }
  const { highestOrder: childStackOrder } = stackTimelineItems(item.children);
  const result = 1 + childStackOrder + 1;
  cache?.set(item.id, result);
  return result;
};

const sortByDateThenId = (a: TimelineItemType, b: TimelineItemType): number => {
  const aDates = getNormalizePlannedDates(a.plannedStartDateTime, a.plannedFinishDateTime);
  const bDates = getNormalizePlannedDates(b.plannedStartDateTime, b.plannedFinishDateTime);
  const aStart = aDates.start ? new Date(aDates.start).getTime() : Infinity;
  const bStart = bDates.start ? new Date(bDates.start).getTime() : Infinity;

  if (aStart !== bStart) {
    return aStart - bStart;
  }

  const aEnd = aDates.finish ? new Date(aDates.finish).getTime() : Infinity;
  const bEnd = bDates.finish ? new Date(bDates.finish).getTime() : Infinity;

  if (aEnd !== bEnd) return bEnd - aEnd;
  return a.id - b.id;
};

export const stackTimelineItems = (
  items: TimelineItemType[],
  itemRanks?: Map<number, string>,
): { stackedItems: ItemWithStackingOrder[]; highestOrder: number } => {
  const sortedItems = [...items].sort((a, b) => {
    if (itemRanks && itemRanks.size > 0) {
      const aRank = itemRanks.get(a.id);
      const bRank = itemRanks.get(b.id);
      if (aRank !== undefined && bRank !== undefined) {
        return aRank.localeCompare(bRank);
      }
      if (aRank !== undefined) return -1;
      if (bRank !== undefined) return 1;
    }
    return sortByDateThenId(a, b);
  });

  const stackedItems: ItemWithStackingOrder[] = [];
  let highestOrder = 0;

  // Track the latest end time per order slot — if current item starts after
  // slot's end time, the slot is free (no overlap).
  const endTimeByOrder: number[] = [];

  for (const item of sortedItems) {
    const dates = getNormalizePlannedDates(item.plannedStartDateTime, item.plannedFinishDateTime);
    const itemStart = dates.start ? new Date(dates.start).getTime() : -Infinity;
    const itemEnd = dates.finish ? new Date(dates.finish).getTime() : Infinity;

    let order = 0;
    while (order < endTimeByOrder.length) {
      const slotEnd = endTimeByOrder[order];
      if (slotEnd !== undefined && itemStart > slotEnd) {
        break;
      }
      order++;
    }

    if (order >= endTimeByOrder.length) {
      endTimeByOrder.push(itemEnd);
    } else {
      const current = endTimeByOrder[order];
      endTimeByOrder[order] = current !== undefined ? Math.max(current, itemEnd) : itemEnd;
    }

    if (order > highestOrder) {
      highestOrder = order;
    }
    stackedItems.push({
      ...item,
      order,
    });
  }

  return { stackedItems, highestOrder };
};
export const calculateMaxLanesForOrder = (
  itemsAtOrder: TimelineItemType[],
  expandedChildren: { [key: number]: boolean },
  cache?: Map<number, number>,
): number => {
  let maxLanesForThisOrder = 1;

  for (const item of itemsAtOrder) {
    if (expandedChildren[item.id] && item.children && item.children.length > 0) {
      const lanes = calculateChildLanes(item, cache);
      maxLanesForThisOrder = Math.max(maxLanesForThisOrder, lanes);
    }
  }

  return maxLanesForThisOrder;
};

export const calculateLanesByOrder = (
  allItems: TimelineItemType[],
  expandedChildren: { [key: number]: boolean },
  cache?: Map<number, number>,
): Map<number, number> => {
  const itemsByOrder = new Map<number, TimelineItemType[]>();

  for (const item of allItems) {
    const order = item.order || 0;
    if (!itemsByOrder.has(order)) {
      itemsByOrder.set(order, []);
    }
    itemsByOrder.get(order)?.push(item);
  }

  const sortedOrders = Array.from(itemsByOrder.keys()).sort((a, b) => a - b);
  const lanesByOrder = new Map<number, number>();

  for (const order of sortedOrders) {
    const itemsAtOrder = itemsByOrder.get(order) || [];
    const maxLanesForThisOrder = calculateMaxLanesForOrder(itemsAtOrder, expandedChildren, cache);
    lanesByOrder.set(order, maxLanesForThisOrder);
  }

  return lanesByOrder;
};
